import TargetedPopups from "@/components/_Routes/TargetedPopups";
import TopBarWrapper from "@/components/_Templates/_NewComponents/TopBarWrapper";
import NoMenuWrapper from "@/components/_Templates/NoMenuWrapper";
import { useHasProperty } from "@/hooks";
import { FLAGS_ENUM } from "@/utils/enums/flags";
import { useFsFlag } from "@flagship.io/react-sdk";
import { lazy } from "react";
import { useSelector } from "react-redux";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import FollowExperienceRunWrapper from "../../_Templates/FollowExperienceRunWrapper";
import GenerateResourcesWrapper from "../../_Templates/GenerateResourcesWrapper";
import RedirectionWrapper from "../../_Templates/RedirectionWrapper";
import { AuthWrapper, SideMenuWrapper } from "../../Wrappers";
import ScrollToTop from "./components/ScrollToTop";
import { isEmpty } from "lodash";

const CreateOrganisation = lazy(() =>
  import("../../_Routes/CreateOrganisation")
);
const Home = lazy(() => import("../../_Routes/Home"));
const Rule = lazy(() => import("../../_Routes/Rule"));
const Rules = lazy(() => import("../../_Routes/Rules"));
const Login = lazy(() => import("../../_Routes/Login"));
const Signup = lazy(() => import("../../_Routes/Signup"));
const Collections = lazy(() => import("../../_Routes/Collections"));
const Placements = lazy(() => import("../../_Routes/Placements"));
const Catalog = lazy(() => import("../../_Routes/Catalog"));
const Campaigns = lazy(() => import("../../_Routes/Campaigns"));
const ConfigureCampaigns = lazy(() =>
  import("../../_Routes/ConfigureCampaigns")
);
const NotFound = lazy(() => import("../../_Routes/NotFound"));
const NotAccount = lazy(() => import("../../_Routes/NotAccount"));
const VerifyEmail = lazy(() => import("../../_Routes/VerifyEmail"));
const Report = lazy(() => import("../../_Routes/Report"));
const Settings = lazy(() => import("../../_Routes/Settings"));
const ConfigureMerchandising = lazy(() =>
  import("../../_Routes/ConfigureMerchandising")
);
const ConfigureRecentlyViewedProducts = lazy(() =>
  import("../../_Routes/RecentlyViewedProducts/Tabs")
);
const ConfigureEmailing = lazy(() => import("../../_Routes/Emailing"));
const ConfigureRecommendations = lazy(() =>
  import("../../_Routes/ConfigureRecommendations")
);
const ConfigureLastSessionRecovery = lazy(() =>
  import("../../_Routes/LastSessionRecovery/Tabs")
);
const ConfigureTargetedPopup = lazy(() =>
  import("../../_Routes/TargetedPopups/Tabs")
);
const Datasets = lazy(() => import("../../_Routes/Datasets"));
const ResetPassword = lazy(() => import("../../_Routes/ResetPassword"));
const RecommendationsSite = lazy(() =>
  import("../../_Routes/RecommendationsSite")
);
const RecommendationsMail = lazy(() =>
  import("../../_Routes/RecommendationsMail")
);
const RecentlyViewedProducts = lazy(() =>
  import("../../_Routes/RecentlyViewedProducts")
);
const AutomaticAuthentication = lazy(() =>
  import("../../_Routes/AutomaticAuthentication/AutomaticAuthentication")
);
const LastSessionRecovery = lazy(() =>
  import("../../_Routes/LastSessionRecovery")
);
const Collection = lazy(() => import("../../_Routes/Collection/Main"));
const Merchandising = lazy(() => import("../../_Routes/Merchandising"));
const TargetedPopups = lazy(() => import("../../_Routes/TargetedPopups"));
const SuperAdmin = lazy(() => import("../../_Routes/SuperAdmin"));
const Variables = lazy(() => import("../../_Routes/Variables"));
const Templates = lazy(() => import("../../_Routes/Templates"));
const ConfigureVariable = lazy(() => import("../../_Routes/ConfigureVariable"));
const Algorithms = lazy(() => import("../../_Routes/Algorithms"));
const RecommendationBuilder = lazy(() =>
  import("../../_Routes/RecommendationBuilder")
);
const MerchandisingBuilder = lazy(() =>
  import("../../_Routes/MerchandisingBuilder")
);
const ConfigureAlgorithm = lazy(() =>
  import("../../_Routes/ConfigureAlgorithm")
);
const RecommendationSettings = lazy(() =>
  import("../../_Routes/RecommendationSettings")
);
const Fields = lazy(() => import("../../_Routes/Fields"));
const FieldBuilder = lazy(() => import("../../_Routes/FieldBuilder"));
const FieldFormulaAI = lazy(() => import("../../_Routes/FieldFormulaAI"));
const RecommendationTemplateBuilder = lazy(() =>
  import("../../_Routes/RecommendationTemplateBuilder")
);

const MerchandisingReport = lazy(() =>
  import("../../_Routes/MerchandisingReport")
);
const DeployRecommendation = lazy(() =>
  import("../../_Routes/DeployRecommendation")
);
const DeployMerchandising = lazy(() =>
  import("../../_Routes/DeployMerchandising")
);
const Integration = lazy(() => import("../../_Routes/CatalogIntegration"));
const CsvUpload = lazy(() => import("../../_Routes/CsvUpload"));
const ConnectAnalytics = lazy(() => import("../../_Routes/ConnectAnalytics"));
const Mapping = lazy(() => import("../../_Routes/Mapping"));

const sideMenu = (wrappedComponent, marginContent = true) => (
  <SideMenuWrapper
    wrappedComponent={wrappedComponent}
    marginContent={marginContent}
  />
);

const noMenu = (wrappedComponent) => (
  <NoMenuWrapper wrappedComponent={wrappedComponent} />
);

const topBarMenu = (wrappedComponent) => (
  <TopBarWrapper wrappedComponent={wrappedComponent} />
);

export function AuthenticatedRoute({ component }) {
  const isLoggedIn = useSelector((state) => state.login.isLoggedIn);
  return isLoggedIn ? <>{component}</> : <Redirect to="/login" />;
}

export function SuperAdminRoute({ component }) {
  const isLoggedIn = useSelector((state) => state.login.isLoggedIn);
  const devMode = useSelector((state) => state.developerMode.isEnabled);

  return isLoggedIn && devMode ? <>{component}</> : <Redirect to="/" />;
}

const Router = () => {
  const { hideReport, hasBuilder } = useHasProperty();

  const flagHasMerchandisingReport = useFsFlag(
    FLAGS_ENUM.HAS_MERCHANDISING_REPORT
  );
  const flagValueHasMerchandisingReport =
    flagHasMerchandisingReport.getValue(false);

  const searchParams = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );
  const redirectTo = searchParams?.["redirect_to"] ?? "";
  const hasRedirectTo = !isEmpty(redirectTo)
    ? new URL(redirectTo).hostname.endsWith("app2.abtasty.com") ||
      new URL(redirectTo).hostname.endsWith("abtasty.io")
    : false;

  return (
    <BrowserRouter>
      <RedirectionWrapper>
        <GenerateResourcesWrapper>
          <FollowExperienceRunWrapper>
            <ScrollToTop />
            <Switch>
              <Route exact path="/">
                <NoMenuWrapper wrappedComponent={Login} />
              </Route>
              <Route exact path="/login">
                <NoMenuWrapper wrappedComponent={Login} />
              </Route>
              <Route path="/signup">
                <AuthWrapper wrappedComponent={Signup} />
              </Route>
              <Route path="/verify">
                <AuthWrapper wrappedComponent={VerifyEmail} />
              </Route>
              <Route exact path="/password/reset">
                <AuthWrapper wrappedComponent={ResetPassword} />
              </Route>
              <Route exact path="/create_organisation">
                <AuthWrapper wrappedComponent={CreateOrganisation} />
              </Route>

              <Route exact path="/:organisationId/:siteId/collections">
                <AuthenticatedRoute component={sideMenu(Collections)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recommendation_builder"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(RecommendationBuilder)
                      : noMenu(RecommendationBuilder)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recommendation/:experienceId/deploy"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(DeployRecommendation)
                      : noMenu(DeployRecommendation)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/merchandising/:experienceId/deploy"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(DeployMerchandising)
                      : noMenu(DeployMerchandising)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/merchandising_builder"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(MerchandisingBuilder)
                      : noMenu(MerchandisingBuilder)
                  }
                />
              </Route>

              <Route
                exact
                path="/:organisationId/:siteId/recommendation_builder/:experienceId"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(RecommendationBuilder)
                      : noMenu(RecommendationBuilder)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/merchandising_builder"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(MerchandisingBuilder)
                      : noMenu(MerchandisingBuilder)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/merchandising_builder/:experienceId"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(MerchandisingBuilder)
                      : noMenu(MerchandisingBuilder)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/collections/manual_collection/:collectionId"
              >
                <AuthenticatedRoute component={sideMenu(Collection)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/collections/manual_collection"
              >
                <AuthenticatedRoute component={sideMenu(Collection)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/collections/composed_collection/:collectionId"
              >
                <AuthenticatedRoute component={sideMenu(Collection)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/collections/composed_collection"
              >
                <AuthenticatedRoute component={sideMenu(Collection)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/collections/dynamic_collection/:collectionId"
              >
                <AuthenticatedRoute component={sideMenu(Collection)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/collections/dynamic_collection"
              >
                <AuthenticatedRoute component={sideMenu(Collection)} />
              </Route>
              <Route path="/:organisationId/:siteId/settings">
                <AuthenticatedRoute
                  component={
                    hasRedirectTo ? topBarMenu(Settings) : sideMenu(Settings)
                  }
                />
              </Route>
              <Route path="/:organisationId/:siteId/csv_upload">
                <AuthenticatedRoute component={noMenu(CsvUpload)} />
              </Route>
              <Route path="/:organisationId/:siteId/integration/:integrationType">
                <AuthenticatedRoute component={noMenu(Integration)} />
              </Route>
              <Route path="/:organisationId/:siteId/mapping">
                <AuthenticatedRoute component={noMenu(Mapping)} />
              </Route>
              <Route path="/:organisationId/:siteId/connect_analytics">
                <AuthenticatedRoute component={noMenu(ConnectAnalytics)} />
              </Route>
              {!hideReport && (
                <Route exact path="/:organisationId/:siteId/reporting">
                  <AuthenticatedRoute
                    component={
                      hasRedirectTo ? topBarMenu(Report) : sideMenu(Report)
                    }
                  />
                </Route>
              )}
              {!hideReport && hasBuilder && flagValueHasMerchandisingReport && (
                <Route
                  exact
                  path="/:organisationId/:siteId/merchandising/:experienceId/reporting"
                >
                  <AuthenticatedRoute
                    component={
                      hasRedirectTo
                        ? topBarMenu(MerchandisingReport)
                        : sideMenu(MerchandisingReport, false)
                    }
                  />
                </Route>
              )}
              {hasBuilder && (
                <Route
                  exact
                  path="/:organisationId/:siteId/field/:fieldId/formula"
                >
                  <AuthenticatedRoute
                    component={
                      hasRedirectTo
                        ? topBarMenu(FieldFormulaAI)
                        : noMenu(FieldFormulaAI)
                    }
                  />
                </Route>
              )}
              <Route exact path="/:organisationId/:siteId/catalog">
                <AuthenticatedRoute
                  component={
                    hasRedirectTo ? topBarMenu(Catalog) : sideMenu(Catalog)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/merchandising/:experienceId"
              >
                <AuthenticatedRoute
                  component={sideMenu(ConfigureMerchandising)}
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recommendations/emailing/:experienceId"
              >
                <AuthenticatedRoute component={sideMenu(ConfigureEmailing)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recommendations/site/:experienceId"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(ConfigureRecommendations)
                      : sideMenu(ConfigureRecommendations)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recently_viewed_products/:experienceId"
              >
                <AuthenticatedRoute
                  component={sideMenu(ConfigureRecentlyViewedProducts)}
                />
              </Route>
              <Route exact path="/:organisationId/:siteId/home">
                <AuthenticatedRoute component={sideMenu(Home)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/targeted_popups/:experienceId"
              >
                <AuthenticatedRoute
                  component={sideMenu(ConfigureTargetedPopup)}
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/last_session_recovery/:experienceId"
              >
                <AuthenticatedRoute
                  component={sideMenu(ConfigureLastSessionRecovery)}
                />
              </Route>

              <Route exact path="/:organisationId/:siteId/recommendations/site">
                <AuthenticatedRoute component={sideMenu(RecommendationsSite)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recommendations/emailing"
              >
                <AuthenticatedRoute component={sideMenu(RecommendationsMail)} />
              </Route>

              <Route exact path="/:organisationId/:siteId/targeted_popups">
                <AuthenticatedRoute component={sideMenu(TargetedPopups)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recently_viewed_products"
              >
                <AuthenticatedRoute
                  component={sideMenu(RecentlyViewedProducts)}
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/merchandising/collection/:collectionId"
              >
                <AuthenticatedRoute component={sideMenu(Collection)} />
              </Route>
              <Route exact path="/:organisationId/:siteId/merchandising">
                <AuthenticatedRoute component={sideMenu(Merchandising)} />
              </Route>

              <Route
                exact
                path="/:organisationId/:siteId/last_session_recovery"
              >
                <AuthenticatedRoute component={sideMenu(LastSessionRecovery)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/automatic_authentication"
              >
                <AuthenticatedRoute
                  component={sideMenu(AutomaticAuthentication)}
                />
              </Route>
              <Route path="/:organisationId/:siteId/super_admin">
                <AuthenticatedRoute component={sideMenu(SuperAdmin)} />
              </Route>
              <Route path="/:organisationId/:siteId/placements">
                <AuthenticatedRoute component={sideMenu(Placements)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/campaigns/:campaignId"
              >
                <AuthenticatedRoute component={sideMenu(ConfigureCampaigns)} />
              </Route>
              <Route path="/:organisationId/:siteId/campaigns">
                <AuthenticatedRoute component={sideMenu(Campaigns)} />
              </Route>
              <Route path="/:organisationId/:siteId/datasets/:datasetId/:fieldId">
                <AuthenticatedRoute component={noMenu(FieldBuilder)} />
              </Route>
              <Route path="/:organisationId/:siteId/datasets/:datasetId">
                <AuthenticatedRoute component={sideMenu(Fields)} />
              </Route>
              <Route path="/:organisationId/:siteId/datasets">
                <AuthenticatedRoute component={sideMenu(Datasets)} />
              </Route>
              <Route exact path="/:organisationId/:siteId/rules">
                <AuthenticatedRoute component={sideMenu(Rules)} />
              </Route>
              <Route exact path="/:organisationId/:siteId/rules/:ruleId">
                <AuthenticatedRoute component={sideMenu(Rule)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/variables/:variableId"
              >
                <AuthenticatedRoute component={sideMenu(ConfigureVariable)} />
              </Route>
              <Route exact path="/:organisationId/:siteId/variables">
                <AuthenticatedRoute component={sideMenu(Variables)} />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recommendation_template_builder/:templateId"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(RecommendationTemplateBuilder)
                      : noMenu(RecommendationTemplateBuilder)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/recommendation_template_builder"
              >
                <AuthenticatedRoute
                  component={
                    hasRedirectTo
                      ? topBarMenu(RecommendationTemplateBuilder)
                      : noMenu(RecommendationTemplateBuilder)
                  }
                />
              </Route>
              <Route exact path="/:organisationId/:siteId/templates">
                <AuthenticatedRoute
                  component={
                    hasRedirectTo ? topBarMenu(Templates) : sideMenu(Templates)
                  }
                />
              </Route>
              <Route
                exact
                path="/:organisationId/:siteId/algorithms/:algorithmId"
              >
                <AuthenticatedRoute component={sideMenu(ConfigureAlgorithm)} />
              </Route>
              <Route exact path="/:organisationId/:siteId/algorithms">
                <AuthenticatedRoute component={sideMenu(Algorithms)} />
              </Route>

              <Route
                exact
                path="/:organisationId/:siteId/recommendations/settings/:experienceId"
              >
                <SuperAdminRoute component={sideMenu(RecommendationSettings)} />
              </Route>
              <Route path="/not_account">
                <NotAccount />
              </Route>
              <Route path="*">
                <NotFound />
              </Route>
            </Switch>
          </FollowExperienceRunWrapper>
        </GenerateResourcesWrapper>
      </RedirectionWrapper>
    </BrowserRouter>
  );
};

export default Router;
